import api from './api';
import { t } from '../i18n';

export interface ForumCategory {
  _id?: string;
  id?: string;
  slug?: string | null;
  name?: string | null;
  description?: string | null;
  icon?: string | null;
  color?: string | null;
  imageUrl?: string | null;
  bannerUrl?: string | null;
  parentCategoryId?: string | null;
  order?: number;
  postingEnabled?: boolean;
  topicCreationEnabled?: boolean;
  topicCount?: number;
  postCount?: number;
  lastTopicId?: string | null;
  lastTopicTitle?: string | null;
  lastTopicSlug?: string | null;
  lastPostTime?: number | null;
  lastPostUserName?: string | null;
  isActive?: boolean;
  isArchived?: boolean;
  subcategories?: ForumCategory[];
}

export interface ForumTopicMedia {
  id?: string;
  type?: 'image' | 'video' | 'document' | 'other';
  url?: string;
  thumbnailUrl?: string | null;
  fileName?: string;
  fileSize?: number;
  mimeType?: string | null;
  width?: number | null;
  height?: number | null;
}

export interface ForumTopic {
  _id?: string;
  id?: string;
  slug?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  categorySlug?: string | null;
  authorId?: string | null;
  authorName?: string | null;
  authorAvatarUrl?: string | null;
  title?: string | null;
  content?: string | null;
  contentPlain?: string | null;
  headerImageUrl?: string | null;
  media?: ForumTopicMedia[];
  tags?: string[];
  isPinned?: boolean;
  isPinnedGlobally?: boolean;
  isLocked?: boolean;
  commentsDisabled?: boolean | null;
  upvotes?: number;
  downvotes?: number;
  score?: number;
  hotScore?: number;
  viewCount?: number;
  postCount?: number;
  uniqueParticipants?: number;
  lastPostTime?: number | null;
  lastPostUserName?: string | null;
  isEdited?: boolean;
  editedAt?: number | null;
  isActive?: boolean;
  isDeleted?: boolean;
  createdAt?: number;
  updatedAt?: number | null;
  userVote?: number;
  isSubscribed?: boolean;
}

export interface ForumPost {
  _id?: string;
  id?: string;
  topicId?: string | null;
  topicTitle?: string | null;
  categoryId?: string | null;
  parentPostId?: string | null;
  rootPostId?: string | null;
  depth?: number;
  path?: string;
  authorId?: string | null;
  authorName?: string | null;
  authorAvatarUrl?: string | null;
  content?: string | null;
  contentPlain?: string | null;
  upvotes?: number;
  downvotes?: number;
  score?: number;
  replyCount?: number;
  isEdited?: boolean;
  editedAt?: number | null;
  isHidden?: boolean;
  isDeleted?: boolean;
  showAsDeleted?: boolean;
  createdAt?: number;
  updatedAt?: number | null;
  userVote?: number;
  replies?: ForumPost[];
}

export interface ForumTopicsResponse {
  topics: ForumTopic[];
  page?: number;
  totalPages?: number;
  total?: number;
  hasMore?: boolean;
}

export interface ForumVoteResult {
  upvotes?: number;
  downvotes?: number;
  score?: number;
}

export interface ForumSubscription {
  _id?: string;
  id?: string;
  userId?: string | null;
  targetType?: 'topic' | 'category' | string | null;
  targetId?: string | null;
  targetName?: string | null;
  targetSlug?: string | null;
  isActive?: boolean;
  createdAt?: number;
}

export interface ForumUserStats {
  _id?: string;
  id?: string;
  userId?: string | null;
  karma?: number;
  postsCreated?: number;
  topicsCreated?: number;
  upvotesReceived?: number;
  downvotesReceived?: number;
  upvotesGiven?: number;
  downvotesGiven?: number;
}

export interface ForumSearchResult {
  topics: ForumTopic[];
  posts: ForumPost[];
}

export type ForumVoteTargetType = 'topic' | 'post';
export type ForumReportReason =
  | 'spam'
  | 'harassment'
  | 'hate_speech'
  | 'misinformation'
  | 'inappropriate'
  | 'off_topic'
  | 'violence'
  | 'copyright'
  | 'other';

export interface CreateTopicPayload {
  categoryId: string;
  title: string;
  content: string;
  headerImageUrl?: string | null;
  tags?: string[];
  media?: ForumTopicMedia[];
}

export interface UpdateTopicPayload {
  title?: string;
  content?: string;
  headerImageUrl?: string | null;
  tags?: string[];
  media?: ForumTopicMedia[];
}

export interface CreatePostPayload {
  topicId: string;
  content: string;
  parentPostId?: string | null;
  media?: ForumTopicMedia[];
}

export interface UpdatePostPayload {
  content: string;
  media?: ForumTopicMedia[];
}

/**
 * Extrahiert eine lesbare Fehlermeldung aus einem Axios-/Backend-Fehler.
 * Das Backend antwortet bei Fehlern i. d. R. mit Status 400/403/404 und
 * einem String-Body (res.send(error.message)).
 */
export function extractForumError(err: unknown, fallback = t('web.service.forum-service.ein-fehler-ist-aufgetreten')): string {
  const anyErr = err as { response?: { data?: unknown; status?: number } } | undefined;
  const data = anyErr?.response?.data;
  if (typeof data === 'string' && data.trim().length > 0) return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (typeof obj.message === 'string' && obj.message) return obj.message;
    if (typeof obj.error === 'string' && obj.error) return obj.error;
  }
  if (anyErr?.response?.status === 401) return t('web.service.forum-service.bitte-melde-dich-an');
  if (anyErr?.response?.status === 403) return t('web.service.forum-service.keine-berechtigung');
  return fallback;
}

class ForumService {
  async getCategories(): Promise<ForumCategory[]> {
    const res = await api.get('/api/forum/categories');
    return Array.isArray(res.data) ? res.data : [];
  }

  async getCategoryTree(): Promise<ForumCategory[]> {
    const res = await api.get('/api/forum/categories/tree');
    return Array.isArray(res.data) ? res.data : [];
  }

  async getCategoryBySlug(slug: string): Promise<ForumCategory | null> {
    try {
      const res = await api.get(`/api/forum/category/${encodeURIComponent(slug)}`);
      return res.data || null;
    } catch {
      return null;
    }
  }

  async getTopics(opts: {
    categoryId?: string;
    page?: number;
    limit?: number;
    sort?: 'newest' | 'hot' | 'top' | 'active' | string;
  } = {}): Promise<ForumTopicsResponse> {
    const params: Record<string, string | number> = {
      page: opts.page ?? 1,
      limit: opts.limit ?? 20,
      sort: opts.sort ?? 'hot',
    };
    if (opts.categoryId) params.categoryId = opts.categoryId;
    const res = await api.get('/api/forum/topics', { params });
    const data = res.data || {};
    return {
      topics: Array.isArray(data.topics) ? data.topics : [],
      page: data.page,
      totalPages: data.totalPages,
      total: data.total,
      hasMore: data.hasMore,
    };
  }

  async getTopicBySlug(slug: string): Promise<ForumTopic | null> {
    try {
      const res = await api.get(`/api/forum/topic/${encodeURIComponent(slug)}`);
      return res.data || null;
    } catch {
      return null;
    }
  }

  async getPostsByTopic(topicId: string, sort: 'best' | 'newest' | 'top' = 'best'): Promise<ForumPost[]> {
    const res = await api.get(
      `/api/forum/topic/${encodeURIComponent(topicId)}/posts`,
      { params: { sort } },
    );
    return Array.isArray(res.data) ? res.data : [];
  }

  // ==================== SCHREIB-FUNKTIONEN (auth via dc-sign Header) ====================

  /**
   * Neues Topic erstellen.
   * Backend-Joi: categoryId + title (5-300) + content (10-50000) required,
   * headerImageUrl/tags/media optional.
   */
  async createTopic(payload: CreateTopicPayload): Promise<ForumTopic> {
    const body: Record<string, unknown> = {
      categoryId: payload.categoryId,
      title: payload.title,
      content: payload.content,
    };
    if (payload.headerImageUrl) body.headerImageUrl = payload.headerImageUrl;
    if (payload.tags && payload.tags.length > 0) body.tags = payload.tags;
    if (payload.media && payload.media.length > 0) body.media = payload.media;
    const res = await api.post('/api/forum/topic', body);
    return (res.data || {}) as ForumTopic;
  }

  /** Topic bearbeiten (nur Autor oder Admin). */
  async updateTopic(topicId: string, payload: UpdateTopicPayload): Promise<ForumTopic> {
    const body: Record<string, unknown> = {};
    if (payload.title !== undefined) body.title = payload.title;
    if (payload.content !== undefined) body.content = payload.content;
    if (payload.headerImageUrl !== undefined) body.headerImageUrl = payload.headerImageUrl;
    if (payload.tags !== undefined) body.tags = payload.tags;
    if (payload.media !== undefined) body.media = payload.media;
    const res = await api.put(`/api/forum/topic/${encodeURIComponent(topicId)}`, body);
    return (res.data || {}) as ForumTopic;
  }

  /** Topic löschen (nur Autor oder Admin). */
  async deleteTopic(topicId: string): Promise<boolean> {
    const res = await api.delete(`/api/forum/topic/${encodeURIComponent(topicId)}`);
    return res.status === 200;
  }

  /**
   * Neuen Post (Antwort/Kommentar) erstellen.
   * Backend-Joi: topicId + content (1-10000) required, parentPostId optional
   * (für verschachtelte Antworten), media optional.
   */
  async createPost(payload: CreatePostPayload): Promise<ForumPost> {
    const body: Record<string, unknown> = {
      topicId: payload.topicId,
      content: payload.content,
    };
    if (payload.parentPostId) body.parentPostId = payload.parentPostId;
    if (payload.media && payload.media.length > 0) body.media = payload.media;
    const res = await api.post('/api/forum/post', body);
    return (res.data || {}) as ForumPost;
  }

  /** Post bearbeiten (nur Autor oder Admin). */
  async updatePost(postId: string, payload: UpdatePostPayload): Promise<ForumPost> {
    const body: Record<string, unknown> = { content: payload.content };
    if (payload.media !== undefined) body.media = payload.media;
    const res = await api.put(`/api/forum/post/${encodeURIComponent(postId)}`, body);
    return (res.data || {}) as ForumPost;
  }

  /** Post löschen (nur Autor oder Admin). */
  async deletePost(postId: string): Promise<boolean> {
    const res = await api.delete(`/api/forum/post/${encodeURIComponent(postId)}`);
    return res.status === 200;
  }

  /**
   * Vote abgeben. Backend-Joi erlaubt NUR voteType 1 oder -1.
   * Toggle-Semantik im Backend: Wird derselbe voteType erneut gesendet,
   * wird der Vote ENTFERNT. Anderer voteType = Vote wird geändert.
   * Eigene Inhalte können nicht gevotet werden (400).
   * Response: { upvotes, downvotes, score }
   */
  async vote(
    targetType: ForumVoteTargetType,
    targetId: string,
    voteType: 1 | -1,
  ): Promise<ForumVoteResult> {
    const res = await api.post('/api/forum/vote', {
      targetType,
      targetId,
      voteType,
    });
    const data = res.data || {};
    return {
      upvotes: typeof data.upvotes === 'number' ? data.upvotes : undefined,
      downvotes: typeof data.downvotes === 'number' ? data.downvotes : undefined,
      score: typeof data.score === 'number' ? data.score : undefined,
    };
  }

  /**
   * Inhalt melden. Backend-Joi: targetType ('topic'|'post'), targetId,
   * reason (Enum) und description (10-1000 Zeichen) sind ALLE required.
   */
  async report(payload: {
    targetType: ForumVoteTargetType;
    targetId: string;
    reason: ForumReportReason;
    description: string;
  }): Promise<boolean> {
    const res = await api.post('/api/forum/report', payload);
    return res.status === 200;
  }

  /** Topic oder Kategorie abonnieren. targetType: 'topic' | 'category'. */
  async subscribe(targetType: 'topic' | 'category', targetId: string): Promise<boolean> {
    const res = await api.post('/api/forum/subscribe', { targetType, targetId });
    return res.status === 200;
  }

  /** Abo beenden. targetType: 'topic' | 'category'. */
  async unsubscribe(targetType: 'topic' | 'category', targetId: string): Promise<boolean> {
    const res = await api.post('/api/forum/unsubscribe', { targetType, targetId });
    return res.status === 200;
  }

  /** Eigene Forum-Statistiken (auth nötig). */
  async getMyStats(): Promise<ForumUserStats | null> {
    try {
      const res = await api.get('/api/forum/me/stats');
      return res.data || null;
    } catch {
      return null;
    }
  }

  /** Eigene Abonnements (auth nötig). */
  async getMySubscriptions(opts: {
    targetType?: 'topic' | 'category';
    page?: number;
    limit?: number;
  } = {}): Promise<ForumSubscription[]> {
    try {
      const params: Record<string, string | number> = {
        page: opts.page ?? 1,
        limit: opts.limit ?? 50,
      };
      if (opts.targetType) params.targetType = opts.targetType;
      const res = await api.get('/api/forum/me/subscriptions', { params });
      return Array.isArray(res.data) ? res.data : [];
    } catch {
      return [];
    }
  }

  /**
   * Forum-Suche. Liefert { topics, posts } oder null bei Fehler
   * (Aufrufer können dann auf Client-seitige Filterung zurückfallen).
   */
  async search(query: string, opts: {
    type?: 'all' | 'topics' | 'posts';
    categoryId?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<ForumSearchResult | null> {
    try {
      const params: Record<string, string | number> = {
        // Backend-Joi-Schema erwartet "query"; "q" zusätzlich für ältere Varianten
        query,
        q: query,
        type: opts.type ?? 'all',
        page: opts.page ?? 1,
        limit: opts.limit ?? 20,
      };
      if (opts.categoryId) params.categoryId = opts.categoryId;
      const res = await api.get('/api/forum/search', { params });
      const data = res.data || {};
      return {
        topics: Array.isArray(data.topics) ? data.topics : [],
        posts: Array.isArray(data.posts) ? data.posts : [],
      };
    } catch (err) {
      console.error('Forum-Suche fehlgeschlagen:', err);
      return null;
    }
  }

  // ==================== HELPER ====================

  /**
   * Extrahiert lesbaren Text aus Forum-Content. Die Flutter-App speichert
   * Inhalte als Fleather/Quill-Delta-JSON ([{insert: "..."}]), die Web-App
   * als Plain-Text. Diese Funktion behandelt beide Formate defensiv.
   */
  extractPlainText(content?: string | null): string {
    if (!content) return '';
    const trimmed = content.trim();
    if (!trimmed.startsWith('[') && !trimmed.startsWith('{')) return content;
    try {
      const parsed = JSON.parse(trimmed);
      const ops: unknown[] = Array.isArray(parsed)
        ? parsed
        : Array.isArray((parsed as { ops?: unknown[] })?.ops)
          ? (parsed as { ops: unknown[] }).ops
          : [];
      if (ops.length === 0) return content;
      let text = '';
      for (const op of ops) {
        const insert = (op as { insert?: unknown })?.insert;
        if (typeof insert === 'string') text += insert;
      }
      return text.trim().length > 0 ? text.trim() : content;
    } catch {
      return content;
    }
  }

  formatRelativeTime(ts?: number | null): string {
    if (!ts) return '';
    const now = Date.now();
    const diff = now - ts;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (diff < 0) return new Date(ts).toLocaleDateString('de-DE');
    if (minutes < 1) return t('web.service.comment-service.gerade-eben');
    if (minutes < 60) return t('web.service.comment-service.vor-min', { minutes });
    if (hours < 24) return t('web.service.comment-service.vor-std', { hours });
    if (days < 7) return t('web.service.forum-service.vor-tg', { days });
    return new Date(ts).toLocaleDateString('de-DE');
  }

  buildFileUrl(fileId?: string | null): string | null {
    if (!fileId) return null;
    if (fileId.startsWith('http://') || fileId.startsWith('https://')) return fileId;
    const base = api.defaults.baseURL || '';
    return `${base}/api/file/${fileId}`;
  }

  /**
   * Baut die Avatar-URL eines Forum-Autors.
   *
   * `authorAvatarUrl` ist trotz des Namens KEINE URL, sondern der rohe
   * Dateiname aus `user.profileImage` (z. B. "c523f292-…-1770658586486.png").
   * Wurde er direkt an <img src> gegeben, lief jeder Request ins Leere und
   * es erschien nur der graue Fallback-Kreis.
   *
   * Identische Konvention wie commentService.buildAvatarUrl:
   * mit Dateiendung → Thumbnail-Variante, ohne → Roh-Datei.
   */
  buildAvatarUrl(fileName?: string | null, size: 100 | 200 | 400 = 100): string | null {
    if (!fileName || typeof fileName !== 'string' || !fileName.trim()) return null;
    const trimmed = fileName.trim();
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    const base = api.defaults.baseURL || '';
    const dotIndex = trimmed.lastIndexOf('.');
    if (dotIndex === -1) return `${base}/api/file/${trimmed}`;
    const basename = trimmed.substring(0, dotIndex);
    const ext = trimmed.substring(dotIndex);
    return `${base}/api/file/thumbnails/${basename}_${size}_${size}_thumbnail${ext}`;
  }
}

export default new ForumService();
